import React, { useState, useEffect, useCallback, memo } from 'react';
import { Plus, Check, X, UserPlus, Loader2, Edit2 } from 'lucide-react';
import { ref, onValue, update, get, off } from 'firebase/database';
import { db } from '../../../../integrations/firebase';

const TeamList = memo(({ currentUser, onSelectChat }) => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [teamTitle, setTeamTitle] = useState("My Teams");
    const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

    // Add Member State
    const [searchUser, setSearchUser] = useState("");
    const [foundUsers, setFoundUsers] = useState([]);
    const [addingUser, setAddingUser] = useState(null);

    // 1. Unified Listener for Title & Teams
    useEffect(() => {
        if (!currentUser?.uid) return;

        const userId = currentUser.uid;
        const titleRef = ref(db, `users/${userId}/teamTitle`);
        const teamRef = ref(db, `users/${userId}/teams`);

        let mounted = true;

        // -- Listener 1: Title --
        const titleListener = onValue(titleRef, (snapshot) => {
            if (mounted && snapshot.exists()) {
                setTeamTitle(snapshot.val());
            }
        });

        // -- Listener 2: Teams --
        const teamListener = onValue(teamRef, async (snapshot) => {
            if (!mounted) return;

            if (snapshot.exists()) {
                const data = snapshot.val();
                const memberIds = Object.keys(data);

                // Optimization: If memberIds didn't change, maybe don't re-fetch deep details?
                // For now, simple fetch is fine as long as we don't set state that triggers this effect.
                // This effect depends ONLY on `currentUser.uid`, which is stable.

                try {
                    const memberPromises = memberIds.map(async (uid) => {
                        try {
                            const userSnap = await get(ref(db, `users/${uid}`));
                            const userData = userSnap.exists() ? userSnap.val() : { name: 'Unknown' };
                            const statusSnap = await get(ref(db, `status/${uid}`));
                            const status = statusSnap.val()?.state || 'offline';
                            return { id: uid, ...userData, status };
                        } catch (e) {
                            return { id: uid, name: 'Unknown', status: 'offline' };
                        }
                    });

                    const validMembers = await Promise.all(memberPromises);
                    if (mounted) {
                        setTeams(validMembers);
                        setLoading(false);
                    }
                } catch (err) {
                    console.error("Error fetching team details:", err);
                    if (mounted) setLoading(false);
                }
            } else {
                if (mounted) {
                    setTeams([]);
                    setLoading(false);
                }
            }
        });

        return () => {
            mounted = false;
            off(titleRef, 'value', titleListener);
            off(teamRef, 'value', teamListener);
        };
    }, [currentUser?.uid]); // Stable dependency

    const handleUpdateTitle = useCallback(async () => {
        if (!teamTitle.trim() || !currentUser?.uid) return;
        try {
            await update(ref(db, `users/${currentUser.uid}`), { teamTitle });
            setIsEditingTitle(false);
        } catch (error) {
            console.error("Error updating team title:", error);
        }
    }, [teamTitle, currentUser?.uid]);

    const handleSearchUsers = useCallback(async (term) => {
        setSearchUser(term);
        if (term.length < 2) {
            setFoundUsers([]);
            return;
        }

        // Debounce logic could be added here, but for now we keep it simple safe
        try {
            const usersRef = ref(db, 'users');
            const snapshot = await get(usersRef);
            if (snapshot.exists()) {
                const all = snapshot.val();
                const matches = Object.entries(all)
                    .filter(([uid, u]) =>
                        uid !== currentUser.uid &&
                        !teams.find(t => t.id === uid) &&
                        u.name.toLowerCase().includes(term.toLowerCase())
                    )
                    .map(([uid, u]) => ({ id: uid, ...u }))
                    .slice(0, 5); // Limit results
                setFoundUsers(matches);
            }
        } catch (e) {
            console.error("Search error", e);
        }
    }, [currentUser?.uid, teams]);

    const handleAddMember = useCallback(async (user) => {
        if (!currentUser?.uid) return;
        setAddingUser(user.id);
        try {
            const updates = {};
            updates[`users/${currentUser.uid}/teams/${user.id}`] = true;
            await update(ref(db), updates);
            setIsAddMemberOpen(false);
            setSearchUser("");
            setFoundUsers([]);
        } catch (error) {
            console.error("Error adding team member:", error);
        } finally {
            setAddingUser(null);
        }
    }, [currentUser?.uid]);

    return (
        <div className="mb-6">
            <div className="flex justify-between items-center mb-3 px-1 group">
                {isEditingTitle ? (
                    <div className="flex items-center gap-1 flex-1 mr-2">
                        <input
                            value={teamTitle}
                            onChange={(e) => setTeamTitle(e.target.value)}
                            className="bg-slate-50 border border-indigo-200 rounded px-2 py-1 text-sm font-bold text-slate-700 w-full focus:outline-none focus:border-indigo-500"
                            autoFocus
                        />
                        <button onClick={handleUpdateTitle} className="text-green-600 hover:bg-green-50 p-1 rounded"><Check size={14} /></button>
                        <button onClick={() => setIsEditingTitle(false)} className="text-red-500 hover:bg-red-50 p-1 rounded"><X size={14} /></button>
                    </div>
                ) : (
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        {teamTitle}
                        <Edit2
                            size={12}
                            className="text-slate-400 cursor-pointer opacity-0 group-hover:opacity-100 hover:text-indigo-600 transition-all"
                            onClick={() => setIsEditingTitle(true)}
                        />
                    </h3>
                )}

                <button
                    onClick={() => setIsAddMemberOpen(!isAddMemberOpen)}
                    className="text-indigo-600 hover:bg-indigo-50 p-1 rounded-full transition-colors"
                >
                    <Plus size={16} />
                </button>
            </div>

            {/* Add Member Dropdown */}
            {isAddMemberOpen && (
                <div className="mb-4 bg-slate-50 p-3 rounded-xl border border-indigo-100 animate-in slide-in-from-top-2">
                    <div className="relative">
                        <UserPlus size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                            placeholder="Search people..."
                            value={searchUser}
                            onChange={(e) => handleSearchUsers(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {foundUsers.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto bg-white rounded-lg border border-slate-100 shadow-sm">
                            {foundUsers.map(user => (
                                <div
                                    key={user.id}
                                    className="p-2 hover:bg-indigo-50 cursor-pointer flex items-center gap-3 transition-colors"
                                    onClick={() => handleAddMember(user)}
                                >
                                    <img src={user.photo || `https://ui-avatars.com/api/?name=${user.name}`} className="w-8 h-8 rounded-full" alt="" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-700 truncate">{user.name}</p>
                                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                    </div>
                                    {addingUser === user.id && <Loader2 size={14} className="animate-spin text-indigo-600" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Team List horizontal scroll */}
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar px-1 min-h-[90px]">
                {loading && teams.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">Loading teams...</div>
                ) : teams.length === 0 ? (
                    <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg p-3 w-full text-center">
                        Tap + to add favorites
                    </div>
                ) : (
                    teams.map(team => (
                        <div
                            key={team.id}
                            className="flex flex-col items-center gap-2 min-w-[60px] cursor-pointer group animate-in fade-in duration-300"
                            onClick={() => onSelectChat(team)}
                        >
                            <div className="relative">
                                <div className="w-14 h-14 rounded-full p-0.5 border-2 border-slate-100 group-hover:border-indigo-500 transition-colors">
                                    <img
                                        src={team.photo || `https://ui-avatars.com/api/?name=${team.name}&background=random&color=fff`}
                                        className="w-full h-full rounded-full object-cover"
                                        alt={team.name}
                                    />
                                </div>
                                <div className={`absolute bottom-1 right-0 w-3.5 h-3.5 border-2 border-white rounded-full ${team.status === 'online' ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                            </div>
                            <span className="text-xs font-semibold text-slate-600 group-hover:text-indigo-600 truncate max-w-[70px] text-center">
                                {team.name}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
});

export default TeamList;
